import React from 'react';
import {
  IoBuildOutline,
  IoBusinessOutline,
  IoCarOutline,
  IoCodeSlashOutline,
  IoCreateOutline,
  IoDocumentOutline,
  IoDocumentTextOutline,
  IoFlaskOutline,
  IoFolderOutline,
  IoGlobeOutline,
  IoGridOutline,
  IoHomeOutline,
  IoImageOutline,
  IoLibraryOutline,
  IoListOutline,
  IoLocationOutline,
  IoMapOutline,
  IoNewspaperOutline,
  IoOpenOutline,
  IoPeopleOutline,
  IoPulseOutline,
  IoReaderOutline,
  IoSearchOutline,
  IoServerOutline,
  IoStatsChartOutline,
  IoTrashOutline,
  IoTrendingUpOutline,
} from 'react-icons/io5';
import { fileKindIcon } from '../viewer/fileIcons';

export const VIZ_TOKEN_RE = /<<<?([^<>]+)>>>?/g;
export const ALLOWED_MIME_TYPES = new Set([
  'application/pdf',
  'text/plain',
  'text/markdown',
  'text/csv',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/tab-separated-values',
  'application/json',
]);
export const ALLOWED_FILE_EXTENSIONS = new Set(['.svg']);
export const MAX_FILE_SIZE = 500 * 1024 * 1024;
export const DEFAULT_CONTEXT_WINDOW_TOKENS = 400000;
export const ATTACHMENT_DUPLICATE_DEBOUNCE_MS = 2000;

const TRANSIENT_TOOL_ACTIVITY_NAMES = new Set(['tool_search', 'tool_describe', 'activate_toolset']);

const TOOL_UI = {
  sql_query: { icon: <IoServerOutline size={14} />, label: 'Data Retrieval' },
  web_search: { icon: <IoSearchOutline size={14} />, label: 'Web Research' },
  code_execution: { icon: <IoCodeSlashOutline size={14} />, label: 'Running Analysis' },
  browser_research_start: { icon: <IoGlobeOutline size={14} />, label: 'Live Web Research' },
  browser_research_status: { icon: <IoPulseOutline size={14} />, label: 'Research Status Check' },
  browser_research_runs: { icon: <IoListOutline size={14} />, label: 'Research Run Lookup' },
  zip_forecast: { icon: <IoTrendingUpOutline size={14} />, label: 'ZIP Forecast' },
  absorption_score: { icon: <IoTrendingUpOutline size={14} />, label: 'Absorption Scoring' },
  absorption_simulate: { icon: <IoFlaskOutline size={14} />, label: 'Absorption Simulation' },
  community_cma: { icon: <IoBusinessOutline size={14} />, label: 'Community Market Research' },
  commute_analysis: { icon: <IoCarOutline size={14} />, label: 'Commute Research' },
  xlsx_create_income_statement: { icon: <IoDocumentTextOutline size={14} />, label: 'Spreadsheet Creation' },
  brief_create: { icon: <IoDocumentTextOutline size={14} />, label: 'Brief Creation' },
  brief_patch: { icon: <IoDocumentTextOutline size={14} />, label: 'Brief Editing' },
  brief_get: { icon: <IoDocumentTextOutline size={14} />, label: 'Brief Retrieval' },
  brief_list: { icon: <IoDocumentTextOutline size={14} />, label: 'Brief Lookup' },
  brief_publish: { icon: <IoOpenOutline size={14} />, label: 'Brief Publishing' },
  create_data_chart: { icon: <IoStatsChartOutline size={14} />, label: 'Chart Creation' },
  create_data_table: { icon: <IoGridOutline size={14} />, label: 'Table Creation' },
  create_data_map: { icon: <IoMapOutline size={14} />, label: 'Map Creation' },
  create_data_point_map: { icon: <IoLocationOutline size={14} />, label: 'Point Map Creation' },
  create_choropleth_map: { icon: <IoGlobeOutline size={14} />, label: 'Choropleth Map Creation' },
  demographics_pack: { icon: <IoPeopleOutline size={14} />, label: 'Demographic Research' },
  describe_table: { icon: <IoReaderOutline size={14} />, label: 'Data Catalog Lookup' },
  generate_image: { icon: <IoImageOutline size={14} />, label: 'Image Generation' },
  files_overview: { icon: <IoFolderOutline size={14} />, label: 'File Review' },
  files_mount: { icon: <IoFolderOutline size={14} />, label: 'File Access' },
  files_unmount: { icon: <IoFolderOutline size={14} />, label: 'File Cleanup' },
  fetch_file: { icon: <IoDocumentOutline size={14} />, label: 'File Retrieval' },
  return_file_to_conversation: { icon: <IoDocumentOutline size={14} />, label: 'Attaching File' },
  inspect_visual_file: { icon: <IoImageOutline size={14} />, label: 'Visual File Review' },
  pdf_page_inventory: { icon: <IoDocumentTextOutline size={14} />, label: 'PDF Page Review' },
  inspect_pdf_pages: { icon: <IoDocumentTextOutline size={14} />, label: 'PDF Page Inspection' },
  knowledge_base: { icon: <IoLibraryOutline size={14} />, label: 'Builder Knowledge Lookup' },
  listing_cma: { icon: <IoHomeOutline size={14} />, label: 'Listing Market Research' },
  macro_econ_pack: { icon: <IoPulseOutline size={14} />, label: 'Macroeconomic Research' },
  notepad_write: { icon: <IoCreateOutline size={14} />, label: 'Note Taking' },
  notepad_get: { icon: <IoDocumentTextOutline size={14} />, label: 'Note Retrieval' },
  notepad_list: { icon: <IoListOutline size={14} />, label: 'Note Lookup' },
  notepad_clear: { icon: <IoTrashOutline size={14} />, label: 'Note Cleanup' },
  compose_research_page: { icon: <IoNewspaperOutline size={14} />, label: 'Research Page Creation' },
  save_file: { icon: <IoDocumentOutline size={14} />, label: 'Saving File' },
  promote_file: { icon: <IoDocumentOutline size={14} />, label: 'Saving Generated File' },
  save_memory: { icon: <IoDocumentTextOutline size={14} />, label: 'Saving Memory' },
  user_coverage: { icon: <IoMapOutline size={14} />, label: 'Coverage Review' },
  vfs_browse: { icon: <IoFolderOutline size={14} />, label: 'Browsing Files' },
  vfs_search: { icon: <IoSearchOutline size={14} />, label: 'Searching Files' },
  vfs_organize: { icon: <IoFolderOutline size={14} />, label: 'Organizing Files' },
  viz_to_image: { icon: <IoImageOutline size={14} />, label: 'Rendering Chart Image' },
  zip_housing_pack: { icon: <IoLocationOutline size={14} />, label: 'ZIP Housing Research' },
  conversation_history_recent: { icon: <IoListOutline size={14} />, label: 'Recent Chat Lookup' },
  conversation_history_search: { icon: <IoSearchOutline size={14} />, label: 'Searching Chat History' },
  conversation_history_read: { icon: <IoDocumentTextOutline size={14} />, label: 'Reading Chat History' },
};

export function isTransientToolActivity(toolName, data = null) {
  const name = String(toolName || data?.tool_name || data?.name || data?.internal_tool_name || '').trim();
  return Boolean(data?.transient || data?.ui_visibility === 'transient' || TRANSIENT_TOOL_ACTIVITY_NAMES.has(name));
}

export function formatTransientToolActivity(toolName) {
  if (toolName === 'tool_search') return 'Atlas is finding the right capabilities for this request.';
  if (toolName === 'tool_describe') return 'Atlas is checking how to use the selected capability.';
  if (toolName === 'activate_toolset') return 'Atlas is loading the selected capability.';
  return 'Atlas is updating its working context.';
}

const TOOL_BADGE_ICONS = {
  sql_query: <IoServerOutline size={18} />,
  web_search: <IoSearchOutline size={18} />,
  code_execution: <IoCodeSlashOutline size={18} />,
  browser_research_start: <IoGlobeOutline size={18} />,
  browser_research_status: <IoPulseOutline size={18} />,
  browser_research_runs: <IoListOutline size={18} />,
  zip_forecast: <IoTrendingUpOutline size={18} />,
  absorption_score: <IoTrendingUpOutline size={18} />,
  absorption_simulate: <IoFlaskOutline size={18} />,
  community_cma: <IoBusinessOutline size={18} />,
  commute_analysis: <IoCarOutline size={18} />,
  xlsx_create_income_statement: <IoDocumentTextOutline size={18} />,
  brief_create: <IoDocumentTextOutline size={18} />,
  brief_patch: <IoDocumentTextOutline size={18} />,
  brief_get: <IoDocumentTextOutline size={18} />,
  brief_list: <IoDocumentTextOutline size={18} />,
  brief_publish: <IoOpenOutline size={18} />,
  create_data_chart: <IoStatsChartOutline size={18} />,
  create_data_table: <IoGridOutline size={18} />,
  create_data_map: <IoMapOutline size={18} />,
  create_data_point_map: <IoLocationOutline size={18} />,
  create_choropleth_map: <IoGlobeOutline size={18} />,
  demographics_pack: <IoPeopleOutline size={18} />,
  describe_table: <IoReaderOutline size={18} />,
  generate_image: <IoImageOutline size={18} />,
  files_overview: <IoFolderOutline size={18} />,
  files_mount: <IoFolderOutline size={18} />,
  files_unmount: <IoFolderOutline size={18} />,
  fetch_file: <IoDocumentOutline size={18} />,
  return_file_to_conversation: <IoDocumentOutline size={18} />,
  inspect_visual_file: <IoImageOutline size={18} />,
  pdf_page_inventory: <IoDocumentTextOutline size={18} />,
  inspect_pdf_pages: <IoDocumentTextOutline size={18} />,
  knowledge_base: <IoLibraryOutline size={18} />,
  listing_cma: <IoHomeOutline size={18} />,
  macro_econ_pack: <IoPulseOutline size={18} />,
  notepad_write: <IoCreateOutline size={18} />,
  notepad_get: <IoDocumentTextOutline size={18} />,
  notepad_list: <IoListOutline size={18} />,
  notepad_clear: <IoTrashOutline size={18} />,
  compose_research_page: <IoNewspaperOutline size={18} />,
  save_file: <IoDocumentOutline size={18} />,
  promote_file: <IoDocumentOutline size={18} />,
  save_memory: <IoDocumentTextOutline size={18} />,
  user_coverage: <IoMapOutline size={18} />,
  vfs_browse: <IoFolderOutline size={18} />,
  vfs_search: <IoSearchOutline size={18} />,
  vfs_organize: <IoFolderOutline size={18} />,
  viz_to_image: <IoImageOutline size={18} />,
  zip_housing_pack: <IoLocationOutline size={18} />,
  conversation_history_recent: <IoListOutline size={18} />,
  conversation_history_search: <IoSearchOutline size={18} />,
  conversation_history_read: <IoDocumentTextOutline size={18} />,
};

const CITE_TAG_RE = /<cite\s+index="([^"]+)">([\s\S]*?)<\/cite>/g;

export function buildShareUrl(shareId) {
  if (!shareId) return '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/share/${shareId}`;
}

export function extractClipboardFiles(clipboardData) {
  if (!clipboardData) return [];
  const out = [];
  const seen = new Set();

  const pushUnique = (file) => {
    if (!file) return;
    const key = buildAttachmentSignature(file);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(file);
  };

  if (clipboardData.files?.length > 0) {
    for (const file of Array.from(clipboardData.files)) {
      pushUnique(file);
    }
  }

  if (clipboardData.items?.length > 0) {
    for (const item of Array.from(clipboardData.items)) {
      if (item.kind !== 'file') continue;
      pushUnique(item.getAsFile());
    }
  }

  return out;
}

export function buildAttachmentSignature(file) {
  if (!file) return '';
  const name = file.name || '';
  const type = file.type || '';

  // Clipboard images are often exposed twice: once through clipboardData.files
  // and once through clipboardData.items, with different lastModified values.
  // Browser-generated names like "image.png" are not user-meaningful, so use a
  // stable signature for these pasted image wrappers.
  if (type.startsWith('image/') && (!name || /^image\.(png|jpe?g|gif|webp)$/i.test(name))) {
    return `clipboard-image:${type}:${file.size || 0}`;
  }

  return `${name}:${file.size}:${type}:${file.lastModified || 0}`;
}

export function isAllowedAttachmentFile(file) {
  if (!file) return false;
  if (ALLOWED_MIME_TYPES.has(file.type)) return true;
  const filename = String(file.name || '').toLowerCase();
  return Array.from(ALLOWED_FILE_EXTENSIONS).some(ext => filename.endsWith(ext));
}

export function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatTokenCompact(value) {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 100) / 10}k`;
  return `${Math.round(n)}`;
}

export function normalizeContextUsage(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const windowTokensRaw = Number(raw.context_window_tokens);
  const softLimitTokensRaw = Number(raw.context_soft_limit_tokens);
  const actualUsedRaw = Number(
    raw.context_actual_used_tokens ?? raw.context_used_tokens
  );
  const displayUsedRaw = Number(raw.context_display_used_tokens);
  const windowTokens = Number.isFinite(windowTokensRaw) && windowTokensRaw > 0
    ? Math.round(windowTokensRaw)
    : DEFAULT_CONTEXT_WINDOW_TOKENS;
  const softLimitTokens = Number.isFinite(softLimitTokensRaw) && softLimitTokensRaw > 0
    ? Math.round(softLimitTokensRaw)
    : Math.min(windowTokens, DEFAULT_CONTEXT_WINDOW_TOKENS);
  const displayLimitTokens = Math.max(1, Math.min(windowTokens, softLimitTokens));
  const usedTokens = Number.isFinite(actualUsedRaw) && actualUsedRaw >= 0 ? Math.round(actualUsedRaw) : null;
  if (usedTokens == null) return null;
  const displayUsedTokens = Number.isFinite(displayUsedRaw) && displayUsedRaw >= 0
    ? Math.max(0, Math.min(displayLimitTokens, Math.round(displayUsedRaw)))
    : Math.max(0, Math.min(displayLimitTokens, usedTokens));
  const remainingTokens = Math.max(0, windowTokens - usedTokens);
  const softRemainingTokens = Math.max(0, softLimitTokens - usedTokens);
  const overageTokens = Math.max(0, usedTokens - softLimitTokens);
  const fillRatio = displayLimitTokens > 0 ? displayUsedTokens / displayLimitTokens : 0;
  return {
    context_window_tokens: windowTokens,
    context_soft_limit_tokens: softLimitTokens,
    context_display_limit_tokens: displayLimitTokens,
    context_used_tokens: usedTokens,
    context_actual_used_tokens: usedTokens,
    context_display_used_tokens: displayUsedTokens,
    context_remaining_tokens: remainingTokens,
    context_soft_remaining_tokens: softRemainingTokens,
    context_overage_tokens: overageTokens,
    context_fill_ratio: fillRatio,
    context_actual_fill_ratio: windowTokens > 0 ? usedTokens / windowTokens : 0,
    turn_total_tokens: Number(raw.turn_total_tokens) || 0,
    turn_input_tokens: Number(raw.turn_input_tokens) || 0,
    turn_output_tokens: Number(raw.turn_output_tokens) || 0,
    api_call_count: Number(raw.api_call_count) || 0,
    context_count_source: raw.context_count_source || null,
    estimated: Boolean(raw.estimated),
  };
}

// The kind -> icon map lives in viewer/fileIcons (shared with the File Browser).
// The filename matters: saved decks and sheets often arrive as octet-stream.
export function getFileTypeIcon(mimeType, filename) {
  return fileKindIcon({ mime_type: mimeType, filename });
}

/**
 * Patch a sent message's file chips with what happened to each attachment,
 * keyed by the chip's pending id (the composer attachment id). The chip list
 * mixes library files, new uploads and references, so its order says nothing
 * about which upload a result belongs to.
 *   outcomes: { [pendingId]: { file_id } | { error } }
 */
export function applyAttachmentOutcomes(uploadedFiles, outcomes) {
  if (!Array.isArray(uploadedFiles) || !outcomes) return uploadedFiles;
  let changed = false;
  const next = uploadedFiles.map((chip) => {
    const key = chip?.pending_id ?? chip?.file_id;
    const outcome = key != null ? outcomes[key] : null;
    if (!outcome) return chip;
    changed = true;
    if (outcome.file_id) {
      const { upload_error: _ignored, ...rest } = chip;
      return { ...rest, file_id: outcome.file_id, upload_status: 'done' };
    }
    return { ...chip, upload_status: 'failed', upload_error: outcome.error || 'Upload failed' };
  });
  return changed ? next : uploadedFiles;
}

/** A File Browser file as a composer attachment (already stored; nothing uploads). */
export function libraryAttachmentFromFile(fileInfo) {
  const isCompany = fileInfo?.workspace_scope_type === 'company' && fileInfo?.company_id != null;
  return {
    id: fileInfo.file_id,
    file: null,
    name: fileInfo.filename,
    size: fileInfo.size_bytes,
    mimeType: fileInfo.mime_type,
    status: 'existing',
    existingFileId: fileInfo.file_id,
    workspaceScopeType: isCompany ? 'company' : 'user',
    companyId: isCompany ? fileInfo.company_id : null,
  };
}

/** Add a library file to the composer list unless it is already there. */
export function addLibraryAttachment(attachments, fileInfo) {
  const list = Array.isArray(attachments) ? attachments : [];
  if (!fileInfo?.file_id) return { attachments: list, added: false, duplicate: false };
  const duplicate = list.some((a) => a.existingFileId === fileInfo.file_id || a.id === fileInfo.file_id);
  if (duplicate) return { attachments: list, added: false, duplicate: true };
  return { attachments: [...list, libraryAttachmentFromFile(fileInfo)], added: true, duplicate: false };
}

export function newId() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export function briefsToVersionFormat(briefs, currentBriefId = null) {
  if (!briefs || !Array.isArray(briefs)) return { versions: [], currentVersionNumber: null };

  const titleCounts = {};
  const titleVersions = {};
  const chronological = [...briefs].reverse();

  chronological.forEach((brief) => {
    const title = brief.title || 'Untitled';
    titleCounts[title] = (titleCounts[title] || 0) + 1;
  });

  const briefVersionMap = {};
  chronological.forEach((brief) => {
    const title = brief.title || 'Untitled';
    if (titleCounts[title] > 1) {
      titleVersions[title] = (titleVersions[title] || 0) + 1;
      briefVersionMap[brief.brief_id] = titleVersions[title];
    }
  });

  const versions = briefs.map((brief, idx) => {
    const title = brief.title || 'Untitled';
    const versionNum = briefVersionMap[brief.brief_id];
    const displayTitle = versionNum ? `${title} (v${versionNum})` : title;

    return {
      version_id: brief.brief_id,
      version_number: briefs.length - idx,
      brief_id: brief.brief_id,
      title: brief.title,
      displayTitle,
      created_at: brief.created_at || brief.updated_at,
      share_id: brief.published_share_id,
    };
  });

  const currentIdx = currentBriefId ? versions.findIndex((version) => version.brief_id === currentBriefId) : -1;
  const currentVersionNumber = currentIdx >= 0 ? versions[currentIdx].version_number : null;
  return { versions, currentVersionNumber };
}

export function truncateBriefTitle(title, maxLen = 20) {
  if (!title || title.length <= maxLen) return title;
  const versionMatch = title.match(/\s*\(v\d+\)$/);
  const versionSuffix = versionMatch ? versionMatch[0] : '';
  const titleWithoutVersion = versionMatch ? title.slice(0, -versionSuffix.length) : title;
  const availableLen = maxLen - versionSuffix.length - 3;
  if (availableLen <= 0) return `${title.substring(0, maxLen - 3)}...`;
  return `${titleWithoutVersion.substring(0, availableLen)}...${versionSuffix}`;
}

export function splitByVizTokens(text) {
  const input = String(text || '');
  const parts = [];
  let lastIdx = 0;
  VIZ_TOKEN_RE.lastIndex = 0;

  let match;
  while ((match = VIZ_TOKEN_RE.exec(input)) !== null) {
    const full = match[0];
    const token = (match[1] || '').trim();
    const idx = match.index;
    if (idx > lastIdx) parts.push({ type: 'markdown', content: input.slice(lastIdx, idx) });
    parts.push({ type: 'token', token, raw: full });
    lastIdx = idx + full.length;
  }
  if (lastIdx < input.length) parts.push({ type: 'markdown', content: input.slice(lastIdx) });
  return parts;
}

export function isVizNameToken(token) {
  if (!token) return false;
  if (token.includes(':')) return false;
  return /^[A-Za-z0-9_]+$/.test(token);
}

export function isImageToken(token) {
  if (!token) return false;
  return token.startsWith('image:');
}

export function getImageId(token) {
  if (!token || !token.startsWith('image:')) return null;
  return token.slice(6).trim();
}

export function normalizeAssistantDelta(prev, delta) {
  const prevText = String(prev || '');
  const nextText = String(delta || '');
  if (!prevText) return nextText;
  if (!nextText) return nextText;

  if (nextText.startsWith('<<<') && !prevText.endsWith('\n')) {
    return `\n\n${nextText}`;
  }
  if (nextText.startsWith('#') && !prevText.endsWith('\n')) {
    return `\n\n${nextText}`;
  }
  if (prevText.endsWith(':') && !/^\s/.test(nextText)) {
    const prevChar = prevText.slice(-2, -1);
    if (/\d/.test(prevChar) && /^\d/.test(nextText)) return nextText;
    if (nextText.startsWith('/') || nextText.startsWith('\\')) return nextText;
    if (nextText.startsWith('#')) return `\n\n${nextText}`;
    if (/^(Now|Next|Then|Also|Here|Let|I')\b/.test(nextText)) return `\n${nextText}`;
    return ` ${nextText}`;
  }
  return nextText;
}

export function rewriteCiteTags(markdown, citationsMap) {
  const input = String(markdown || '');
  const map = citationsMap && typeof citationsMap === 'object' ? citationsMap : {};

  return input.replace(CITE_TAG_RE, (_match, rawIndex, rawInner) => {
    const index = String(rawIndex || '').trim();
    const inner = String(rawInner || '').trim();
    if (!inner) return '';

    const src = map[index];
    const url = src?.url;
    const title = src?.title;

    if (url) {
      const label = title || url;
      return `${inner} [${label}](${url})`;
    }
    return inner;
  });
}

export function parseToolInput(toolInput) {
  if (!toolInput) return {};
  if (typeof toolInput === 'string') {
    try {
      const parsed = JSON.parse(toolInput);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return toolInput && typeof toolInput === 'object' ? toolInput : {};
}

export function safeInputString(value, fallback = '') {
  if (value == null) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}

export function normalizeZipCode(zip) {
  const raw = safeInputString(zip, '').trim();
  if (!raw) return '';
  return raw.padStart(5, '0');
}

function getCommuteBadgeKey(toolInput) {
  const input = parseToolInput(toolInput);
  const mode = safeInputString(input.mode, 'overview').trim().toLowerCase();
  const direction = safeInputString(input.direction, 'outbound').trim().toLowerCase();

  if (mode === 'flows') {
    const normalizedDirection = ['inbound', 'outbound', 'both'].includes(direction) ? direction : 'outbound';
    return `commute_analysis:flows:${normalizedDirection}`;
  }
  if (mode === 'sectors') return 'commute_analysis:sectors';
  if (mode === 'growth') return 'commute_analysis:growth';
  if (mode === 'compare') return 'commute_analysis:compare';
  return 'commute_analysis:overview';
}

function getCommuteBadgePresentation(badgeKey) {
  switch (badgeKey) {
    case 'commute_analysis:flows:outbound':
      return {
        label: 'Mapped workplace destinations',
        icon: <IoBusinessOutline size={14} />,
        badgeIcon: <IoBusinessOutline size={18} />,
      };
    case 'commute_analysis:flows:inbound':
      return {
        label: 'Mapped worker home locations',
        icon: <IoHomeOutline size={14} />,
        badgeIcon: <IoHomeOutline size={18} />,
      };
    case 'commute_analysis:flows:both':
      return {
        label: 'Mapped commute flows',
        icon: <IoMapOutline size={14} />,
        badgeIcon: <IoMapOutline size={18} />,
      };
    case 'commute_analysis:sectors':
      return {
        label: 'Analyzed sector composition',
        icon: <IoStatsChartOutline size={14} />,
        badgeIcon: <IoStatsChartOutline size={18} />,
      };
    case 'commute_analysis:growth':
      return {
        label: 'Analyzed growth trends',
        icon: <IoTrendingUpOutline size={14} />,
        badgeIcon: <IoTrendingUpOutline size={18} />,
      };
    case 'commute_analysis:compare':
      return {
        label: 'Compared commute markets',
        icon: <IoStatsChartOutline size={14} />,
        badgeIcon: <IoStatsChartOutline size={18} />,
      };
    default:
      return {
        label: 'Analyzed commute patterns',
        icon: <IoCarOutline size={14} />,
        badgeIcon: <IoCarOutline size={18} />,
      };
  }
}

export function getToolBadgeKey(toolName, toolInput) {
  if (toolName === 'commute_analysis') return getCommuteBadgeKey(toolInput);
  return toolName;
}

export function getToolBadgePresentation(toolName, badgeKey) {
  if (toolName === 'commute_analysis') {
    return getCommuteBadgePresentation(badgeKey || 'commute_analysis:overview');
  }
  const ui = TOOL_UI[toolName] || { icon: <IoBuildOutline size={14} />, label: toolName };
  const badgeIcon = TOOL_BADGE_ICONS[toolName] || ui.icon || <IoBuildOutline size={18} />;
  return { label: ui.label, icon: ui.icon, badgeIcon };
}

export function normalizeToolBadgeStatus(status, { running = false } = {}) {
  const normalized = String(status || '').trim().toLowerCase();
  if (running || normalized === 'running' || normalized === 'in_progress') return 'running';
  if (normalized === 'error' || normalized === 'failed' || normalized === 'failure') return 'error';
  if (normalized === 'interrupted' || normalized === 'cancelled' || normalized === 'canceled') return 'interrupted';
  return 'done';
}

export function formatToolBadgeInputSummary(toolName, toolInput) {
  const input = parseToolInput(toolInput);
  const zipCode = normalizeZipCode(input.zip_code || input.zip);
  const address = safeInputString(input.address, '').trim();
  const locationId = safeInputString(input.location_id, '').trim();
  const countyFips = safeInputString(input.county_fips, '').trim();
  const mode = safeInputString(input.mode, '').trim();
  const direction = safeInputString(input.direction, '').trim();
  const compareCountyFips = safeInputString(input.compare_county_fips, '').trim();
  const propertyType = safeInputString(input.property_type, '').trim();
  const radius =
    typeof input.radius_miles === 'number' || typeof input.radius_miles === 'string'
      ? safeInputString(input.radius_miles, '').trim()
      : '';

  if (toolName === 'notepad_write') return 'Wrote a note';
  if (toolName === 'describe_table') return 'Looked up the data catalog';
  if (toolName === 'sql_query') return 'Retrieved data from Atlas datasets';
  if (toolName === 'web_search') {
    const query = safeInputString(input.query, '').trim();
    return query ? `Researched the web: "${query}"` : 'Researched the web';
  }

  if (toolName === 'macro_econ_pack') {
    if (countyFips) return `Researched macroeconomic conditions for county ${countyFips}`;
    if (zipCode) return `Researched macroeconomic conditions for ZIP ${zipCode}`;
    if (locationId) return `Researched macroeconomic conditions for location ${locationId}`;
    return 'Researched macroeconomic conditions';
  }

  if (toolName === 'zip_housing_pack') {
    if (zipCode) return `Researched ZIP housing trends for ${zipCode}`;
    if (locationId) return `Researched ZIP housing trends for location ${locationId}`;
    return 'Researched ZIP housing trends';
  }

  if (toolName === 'demographics_pack') {
    if (zipCode) return `Researched demographics for ${zipCode}`;
    if (locationId) return `Researched demographics for location ${locationId}`;
    return 'Researched demographics';
  }

  if (toolName === 'listing_cma') {
    const scope = address
      ? `for ${address}`
      : zipCode
      ? `for ZIP ${zipCode}`
      : locationId
      ? `for location ${locationId}`
      : 'for selected area';
    const extras = [radius ? `${radius} mi` : '', propertyType || ''].filter(Boolean);
    return `Researched listing comps ${scope}${extras.length ? ` • ${extras.join(' • ')}` : ''}`;
  }

  if (toolName === 'community_cma') {
    const scope = address
      ? `near ${address}`
      : zipCode
      ? `near ZIP ${zipCode}`
      : locationId
      ? `near location ${locationId}`
      : 'near selected area';
    const extras = [radius ? `${radius} mi` : '', propertyType || ''].filter(Boolean);
    return `Researched community comps ${scope}${extras.length ? ` • ${extras.join(' • ')}` : ''}`;
  }

  if (toolName === 'commute_analysis') {
    const scope = countyFips ? `county ${countyFips}` : zipCode ? `ZIP ${zipCode}` : 'selected area';
    const extras = [];
    if (mode) extras.push(mode);
    if (direction && mode.toLowerCase() === 'flows') extras.push(direction);
    if (compareCountyFips) extras.push(`vs ${compareCountyFips}`);
    if (radius && (input.focus_lat != null || input.focus_lon != null)) extras.push(`${radius} mi focus`);
    return `Researched commute patterns for ${scope}${extras.length ? ` • ${extras.join(' • ')}` : ''}`;
  }

  if (toolName === 'create_data_table') {
    const tableName = safeInputString(input.table_name, '').trim();
    const rowCount = Array.isArray(input.rows) ? input.rows.length : null;
    const base = tableName ? `Built table ${tableName}` : 'Built a data table';
    return typeof rowCount === 'number' ? `${base} • ${rowCount} rows` : base;
  }

  if (toolName === 'knowledge_base') {
    const fn = safeInputString(input.function_id, '').trim();
    return fn ? `Retrieved builder knowledge: ${fn}` : 'Retrieved builder knowledge';
  }

  if (toolName === 'absorption_simulate') {
    const horizon = safeInputString(input.horizon_months, '').trim();
    const scope = zipCode ? `for ZIP ${zipCode}` : 'for selected market';
    return `Simulated absorption ${scope}${horizon ? ` • ${horizon} months` : ''}`;
  }

  if (toolName === 'fetch_file') return 'Retrieved a file';
  if (toolName === 'promote_file') return 'Saved a generated file to the File Library';
  if (toolName === 'inspect_visual_file') return 'Reviewed an image or PDF';
  if (toolName === 'pdf_page_inventory') return 'Checked PDF pages';
  if (toolName === 'inspect_pdf_pages') return 'Reviewed selected PDF pages';
  if (toolName === 'viz_to_image') {
    const names = Array.isArray(input.viz_names) ? input.viz_names : input.viz_name ? [input.viz_name] : [];
    return names.length > 1 ? `Rendered ${names.length} chart images` : 'Rendered a chart image';
  }
  if (toolName === 'user_coverage') {
    return zipCode ? `Reviewed coverage near ZIP ${zipCode}` : 'Reviewed available coverage';
  }
  if (toolName === 'vfs_browse') {
    return input.folder_id ? 'Browsed a file folder' : 'Browsed the File Library';
  }
  if (toolName === 'vfs_search') {
    const query = safeInputString(input.query, '').trim();
    const category = safeInputString(input.category, '').trim();
    const tag = safeInputString(input.tag, '').trim();
    const term = query || category || tag;
    return term ? `Searched files for "${term}"` : 'Searched files';
  }
  if (toolName === 'vfs_organize') {
    const opCount = Array.isArray(input.operations) ? input.operations.length : 0;
    const action = safeInputString(input.action, '').replace(/_/g, ' ').trim();
    if (opCount) return `Organized files • ${opCount} action${opCount === 1 ? '' : 's'}`;
    return action ? `Organized files • ${action}` : 'Organized files';
  }
  if (toolName === 'conversation_history_recent') return 'Looked up recent chats';
  if (toolName === 'conversation_history_search') return 'Searched chat history';
  if (toolName === 'conversation_history_read') return 'Read prior chat context';

  return '';
}

export function buildToolBadgesFromToolTraces(toolTraces) {
  if (!Array.isArray(toolTraces) || toolTraces.length === 0) return [];
  const grouped = new Map();

  for (const trace of toolTraces) {
    const calls = Array.isArray(trace?.calls) && trace.calls.length > 0
      ? trace.calls
      : Array.isArray(trace?.tool_names)
      ? trace.tool_names.map((name) => ({ tool_name: name, status: 'unknown' }))
      : [];

    for (const call of calls) {
      const toolName = call?.tool_name || call?.name;
      if (!toolName) continue;
      if (isTransientToolActivity(toolName, call)) continue;
      const badgeKey = getToolBadgeKey(toolName, null);
      const presentation = getToolBadgePresentation(toolName, badgeKey);
      const displayLabel = presentation.label || toolName;
      const status = normalizeToolBadgeStatus(call?.status);
      const existing = grouped.get(badgeKey);

      if (existing) {
        existing.count += 1;
        existing.lastStatus = existing.lastStatus === 'error' || status === 'error' ? 'error' : status;
      } else {
        grouped.set(badgeKey, {
          id: `hist_tool_${badgeKey}`,
          toolName,
          badgeKey,
          displayLabel,
          count: 1,
          runningCount: 0,
          lastStatus: status,
          recentInputSummaries: [],
          lastInputSummary: '',
        });
      }
    }
  }

  return Array.from(grouped.values());
}

// Shared by the foreground handler and background accumulation so a save_file
// result produces an identical chip no matter where the stream is attached.
// Tolerates output arriving as a JSON string (Redis relay / serialization
// variants) — a silently skipped chip is exactly the bug this guards against.
export function extractSavedFileFromToolResult(data) {
  const toolName = data?.tool_name || data?.name;
  if (toolName !== 'save_file' && toolName !== 'return_file_to_conversation') return null;
  let output = data?.output;
  if (typeof output === 'string') {
    try { output = JSON.parse(output); } catch { return null; }
  }
  if (!output || typeof output !== 'object') return null;
  if (!output.ok || !output.file_id || output.visibility === 'hidden') return null;
  const fileInfo = {
    file_id: output.file_id,
    filename: output.filename,
    size_bytes: output.size_bytes,
    mime_type: output.mime_type,
    description: output.description,
    download_url: output.download_url,
    source: output.source || (toolName === 'return_file_to_conversation' ? 'return_file' : 'save_file'),
    replaces_file_id: output.replaces_file_id || null,
    root_file_id: output.root_file_id || null,
    version_number: output.version_number || 1,
    library_scope_type: output.library_scope_type || null,
    library_scope_id: output.library_scope_id ?? null,
    company_id: output.company_id ?? null,
    file_type: output.file_type || 'saved',
    status: output.status || 'ready',
  };
  if ((output.version_number || 1) >= 2) {
    fileInfo.version_badge = { version: output.version_number, isLatest: true };
  }
  return fileInfo;
}

// E2B sandbox path: save_file runs via the tool bridge inside code_execution,
// so the file surfaces as a files_saved array on the code_execution result
// instead of its own save_file tool_result. The backend also replays these as
// synthetic save_file events; this direct read is the fallback (dedupe by
// file_id makes the two sources idempotent).
export function extractSavedFilesFromCodeExecution(data) {
  const toolName = data?.tool_name || data?.name;
  if (toolName !== 'code_execution') return [];
  let output = data?.output;
  if (typeof output === 'string') {
    try { output = JSON.parse(output); } catch { return []; }
  }
  const saved = output && typeof output === 'object' ? output.files_saved : null;
  if (!Array.isArray(saved)) return [];
  return saved
    .filter((f) => f && typeof f === 'object' && f.file_id && f.visibility !== 'hidden')
    .map((f) => extractSavedFileFromToolResult({
      tool_name: f.tool_name || 'save_file',
      output: { ok: true, ...f },
    }))
    .filter(Boolean);
}

// Append a saved-file chip to a list, dropping the version it replaces and
// deduping by file_id. Returns a new array.
export function applySavedFileToList(list, fileInfo) {
  const prev = Array.isArray(list) ? list : [];
  const next = fileInfo.replaces_file_id
    ? prev.filter((f) => f.file_id !== fileInfo.replaces_file_id)
    : [...prev];
  if (next.some((f) => f.file_id === fileInfo.file_id)) return next;
  next.push(fileInfo);
  return next;
}

export function accumulateBackgroundEvent(evt, bgEntry, cid, conversationCacheRef) {
  const { type, data } = evt;
  if (!data) return;

  switch (type) {
    case 'text_delta': {
      const delta = data.delta || '';
      const normalized = normalizeAssistantDelta(bgEntry.accumulatedContent, delta);
      bgEntry.accumulatedContent += normalized;
      break;
    }
    case 'tool_call': {
      const toolName = data.name || data.tool_name;
      const toolUseId = data.tool_use_id;
      const toolInput = data.input;
      if (!toolName) break;
      if (isTransientToolActivity(toolName, data)) break;
      const badgeKey = getToolBadgeKey(toolName, toolInput);
      const presentation = getToolBadgePresentation(toolName, badgeKey);
      const displayLabel = presentation.label || toolName;
      const inputSummary = formatToolBadgeInputSummary(toolName, toolInput);

      if (toolUseId) {
        bgEntry.toolRuns[toolUseId] = { toolName, badgeKey, displayLabel, status: 'running' };
      }

      const existing = bgEntry.toolBadges.find((badge) => (badge.badgeKey || badge.toolName) === badgeKey);
      if (existing) {
        existing.count = (existing.count || 0) + 1;
        existing.runningCount = (existing.runningCount || 0) + 1;
        existing.lastStatus = 'running';
        if (inputSummary) {
          const summaries = Array.isArray(existing.recentInputSummaries) ? existing.recentInputSummaries : [];
          summaries.push(inputSummary);
          existing.recentInputSummaries = summaries.slice(-3);
          existing.lastInputSummary = inputSummary;
        }
      } else {
        bgEntry.toolBadges.push({
          id: `bg_${badgeKey}_${Date.now()}`,
          toolName,
          badgeKey,
          displayLabel,
          count: 1,
          runningCount: 1,
          lastStatus: 'running',
          recentInputSummaries: inputSummary ? [inputSummary] : [],
          lastInputSummary: inputSummary || '',
        });
      }
      break;
    }
    case 'tool_result': {
      if (isTransientToolActivity(data.tool_name || data.name, data)) break;
      const toolUseId = data.tool_use_id;
      const hadError =
        data.is_error ||
        (data.output && typeof data.output === 'object' && data.output.type === 'tool_error') ||
        (data.output && typeof data.output === 'object' && data.output.ok === false);
      if (toolUseId && bgEntry.toolRuns[toolUseId]) {
        const run = bgEntry.toolRuns[toolUseId];
        run.status = hadError ? 'error' : 'done';
        const badge = bgEntry.toolBadges.find((item) => (item.badgeKey || item.toolName) === run.badgeKey);
        if (badge) {
          badge.runningCount = Math.max(0, (badge.runningCount || 0) - 1);
          badge.lastStatus = hadError ? 'error' : badge.runningCount > 0 ? 'running' : 'done';
        }
      }
      // File chips must survive backgrounding — losing them here is why files
      // "don't populate until refresh" when the user switches conversations
      // during a long turn.
      const bgFileInfo = extractSavedFileFromToolResult(data);
      if (bgFileInfo) {
        bgEntry.savedFiles = applySavedFileToList(bgEntry.savedFiles, bgFileInfo);
      }
      for (const bridged of extractSavedFilesFromCodeExecution(data)) {
        bgEntry.savedFiles = applySavedFileToList(bgEntry.savedFiles, bridged);
      }
      break;
    }
    case 'citations_update': {
      if (data.citation_map) {
        bgEntry.citationsMap = { ...bgEntry.citationsMap, ...data.citation_map };
      }
      break;
    }
    case 'final_response': {
      bgEntry.completed = true;
      bgEntry.finalData = data;
      if ((!bgEntry.accumulatedContent || !String(bgEntry.accumulatedContent).trim()) && typeof data.response === 'string') {
        bgEntry.accumulatedContent = data.response;
      }
      if (data.context_usage) bgEntry.contextUsage = data.context_usage;
      const cached = conversationCacheRef.current.get(cid);
      if (cached) {
        const finalMessages = cached.messages.map((message) => {
          if (message.id !== bgEntry.assistantMsgId) return message;
          let savedFiles = message.savedFiles;
          for (const fileInfo of bgEntry.savedFiles || []) {
            savedFiles = applySavedFileToList(savedFiles, fileInfo);
          }
          return {
            ...message,
            dbMessageId: data.assistant_message_id || message.dbMessageId || null,
            content: bgEntry.accumulatedContent,
            finalized: true,
            toolBadges: bgEntry.toolBadges.map((badge) => ({ ...badge, runningCount: 0, lastStatus: 'done' })),
            toolRuns: bgEntry.toolRuns,
            citationsMap: bgEntry.citationsMap,
            ...(savedFiles ? { savedFiles } : {}),
          };
        });
        conversationCacheRef.current.set(cid, {
          ...cached,
          messages: finalMessages,
          contextUsage: data.context_usage || cached.contextUsage,
          loadedAt: Date.now(),
        });
      }
      break;
    }
    case 'conversation_started': {
      if (data.conversation_id && data.conversation_id !== cid) {
        const bgMap = bgEntry._bgMapRef;
        if (bgMap) {
          bgMap.delete(cid);
          bgMap.set(data.conversation_id, bgEntry);
        }
      }
      break;
    }
    default:
      break;
  }
}

export function formatToolBadgeTooltip(label, badge) {
  const lines = [label || badge?.toolName || 'Tool activity'];
  const recentSummaries = Array.isArray(badge?.recentInputSummaries)
    ? badge.recentInputSummaries.filter((summary) => typeof summary === 'string' && summary.trim()).slice(-3)
    : badge?.lastInputSummary
    ? [badge.lastInputSummary]
    : [];

  if (recentSummaries.length === 1) {
    lines.push(recentSummaries[0]);
  } else if (recentSummaries.length > 1) {
    lines.push('Recent runs:');
    [...recentSummaries].reverse().forEach((summary, idx) => {
      lines.push(`${idx + 1}. ${summary}`);
    });
  }

  if ((badge?.count || 0) > 1) lines.push(`${badge.count} runs in this response`);
  return lines.join('\n');
}

export function formatToolCall(toolName, toolInput) {
  const input = parseToolInput(toolInput);
  const getString = safeInputString;

  if (toolName === 'brief_create') {
    const title = getString(input.title, 'Brief');
    return `Creating brief: "${title}"`;
  }
  if (toolName === 'brief_patch') return 'Updating brief';
  if (toolName === 'brief_get') return 'Loading brief';
  if (toolName === 'brief_list') return 'Checking briefs';
  if (toolName === 'brief_publish') return 'Publishing brief';

  if (toolName === 'create_data_chart') {
    const title = getString(input.title) || getString(input.chart_name) || 'chart';
    return `Creating chart: "${title}"`;
  }
  if (toolName === 'create_data_map') {
    const title = getString(input.title) || getString(input.map_name) || 'map';
    return `Creating map: "${title}"`;
  }
  if (toolName === 'create_choropleth_map') {
    const title = getString(input.title) || getString(input.map_name) || 'choropleth';
    const geo = getString(input.geography_level);
    return `Creating choropleth: "${title}"${geo ? ` (${geo})` : ''}`;
  }
  if (toolName === 'create_data_point_map') {
    const title = getString(input.title) || getString(input.map_name) || 'point map';
    return `Creating point map: "${title}"`;
  }
  if (toolName === 'zip_forecast') {
    const zip = getString(input.zip);
    const normalizedZip = zip ? zip.padStart(5, '0') : '';
    return `Forecasting ZIP${normalizedZip ? ` ${normalizedZip}` : ''}`;
  }
  if (toolName === 'sql_query') return 'Retrieving data';
  if (toolName === 'web_search') {
    const query = getString(input.query).trim();
    return `Researching the web${query ? `: "${query}"` : ''}`;
  }
  if (toolName === 'save_memory') return 'Saving a memory';
  if (toolName === 'fetch_file') return 'Retrieving file';
  if (toolName === 'promote_file') return 'Saving generated file to File Library';
  if (toolName === 'inspect_visual_file') return 'Reviewing visual file';
  if (toolName === 'pdf_page_inventory') return 'Checking PDF pages';
  if (toolName === 'inspect_pdf_pages') return 'Reviewing PDF pages';
  if (toolName === 'viz_to_image') return 'Rendering chart image';
  if (toolName === 'user_coverage') return 'Reviewing coverage';
  if (toolName === 'vfs_browse') return 'Browsing files';
  if (toolName === 'vfs_search') return 'Searching files';
  if (toolName === 'vfs_organize') return 'Organizing files';
  if (toolName === 'conversation_history_recent') return 'Looking up recent chats';
  if (toolName === 'conversation_history_search') return 'Searching chat history';
  if (toolName === 'conversation_history_read') return 'Reading prior chat context';

  const ui = TOOL_UI[toolName];
  return ui?.label || toolName;
}

export function formatToolResult(toolName, output) {
  const ui = TOOL_UI[toolName] || { label: toolName };
  if (output && typeof output === 'object' && output.ok === false) {
    // No error text in the live status line; the details stay in Tool history.
    return `${ui.label}: trying another way`;
  }
  if (toolName === 'brief_create' && output && typeof output === 'object') {
    const title = output?.brief?.title;
    return `✅ Brief created${title ? `: ${title}` : ''}`;
  }
  if (toolName === 'brief_patch') return '✅ Brief updated';
  if (toolName === 'brief_get') return '✅ Brief loaded';
  if (toolName === 'brief_list') return '✅ Briefs loaded';
  if (toolName === 'brief_publish' && output && typeof output === 'object') {
    const shareId = output?.published?.share_id;
    return `✅ Brief shared${shareId ? ` (${shareId})` : ''}`;
  }
  if (toolName === 'sql_query' && output && typeof output === 'object') {
    const rows = output.rows;
    const status = output.status;
    if (typeof rows === 'number' && status) {
      return `✅ Data retrieved: ${rows.toLocaleString()} row(s) (${status})`;
    }
    if (typeof rows === 'number') return `✅ Data retrieved: ${rows.toLocaleString()} row(s)`;
    return '✅ Data retrieved';
  }
  if (toolName === 'zip_forecast' && output && typeof output === 'object') {
    const zip = output.zip ? String(output.zip).padStart(5, '0') : '';
    const asOf = output.as_of ? `, as of ${output.as_of}` : '';
    return `✅ ZIP forecast ready${zip ? ` (${zip}${asOf})` : ''}`;
  }
  if (toolName === 'create_data_chart') return '✅ Chart ready';
  if (toolName === 'create_data_map') return '✅ Map ready';
  if (toolName === 'create_data_point_map') return '✅ Map ready';
  if (toolName === 'create_choropleth_map') return '✅ Choropleth ready';
  if (toolName === 'generate_image') return '✅ Image ready';
  if (toolName === 'files_overview') return '✅ Files ready';
  if (toolName === 'compose_research_page') return '✅ Research page ready';
  if (toolName === 'web_search') {
    const count = output?.results?.length;
    if (typeof count === 'number') return `✅ Web research complete: ${count} result(s)`;
    return '✅ Web research complete';
  }
  if (toolName === 'save_file' && output && typeof output === 'object') {
    const filename = output.filename;
    if (output.ok && filename) return `✅ File saved: ${filename}`;
    if (output.ok) return '✅ File saved';
  }
  if (toolName === 'return_file_to_conversation' && output && typeof output === 'object') {
    const filename = output.filename;
    if (output.ok && filename) return `✅ File attached: ${filename}`;
    if (output.ok) return '✅ File attached';
  }
  if (toolName === 'save_memory') {
    if (output && typeof output === 'object' && output.ok) return '✅ Memory saved';
    return '⚠️ Memory save failed';
  }
  if (toolName === 'fetch_file') return '✅ File retrieved';
  if (toolName === 'promote_file') return '✅ Generated file saved';
  if (toolName === 'inspect_visual_file') return '✅ Visual file reviewed';
  if (toolName === 'pdf_page_inventory') return '✅ PDF pages checked';
  if (toolName === 'inspect_pdf_pages') return '✅ PDF pages reviewed';
  if (toolName === 'viz_to_image') return '✅ Chart image rendered';
  if (toolName === 'user_coverage') return '✅ Coverage reviewed';
  if (toolName === 'vfs_browse') return '✅ Files browsed';
  if (toolName === 'vfs_search') return '✅ Files searched';
  if (toolName === 'vfs_organize') return '✅ Files organized';
  if (toolName === 'conversation_history_recent') return '✅ Recent chats retrieved';
  if (toolName === 'conversation_history_search') return '✅ Chat history searched';
  if (toolName === 'conversation_history_read') return '✅ Prior chat context loaded';
  return `✅ ${ui.label} complete`;
}
